<template>
	<div class="row inputs-containers">
		<div class="row input-container vat-input-container">
			<p v-if="!withoutVat">
				{{ getString(COUNTRY === 'IT' ? 'vat' : 'vat_tax_id') }}
				<br/>
				<span class="grey">{{ getString('vat_autofill_company_hint') }}</span>
				<br v-if="allowNoVat && COUNTRY === 'IT'"/>
				<span v-if="allowNoVat && COUNTRY === 'IT'" class="primary" @click="toggleWithoutVat"><small>{{ getString('company_without_vatid') }}</small></span>
			</p>
			<div v-if="!withoutVat" class="row centered flex-end">
				<AppLoader v-show="loadingVat" :size="22" class="mr-10"/>
				<AppButton @click="toggleCountryBottomSheet" :paused="disallowOtherCountries" outline :text="company?.country_code || COUNTRY" class="country-select" :class="{'no-pointer': disallowOtherCountries}" />
				<AppTextInput :value="company?.vat" class="vat-input" ref="vat-input" :class="{disabled: loadingVat}" :placeholder="getString('vat_example')" @changed-text="timeoutChangedVat" />
			</div>
			<p v-if="withoutVat" class="mt-10 mb-15">
				<span class="primary" @click="toggleWithoutVat">{{ getString('company_with_vatid') }}</span>
			</p>
		</div>
		<div class="vat-dropdown-container">
			<AppDropdown :collapsed="collapsedDropdown">
				<AppList :class="{disabled: loadingVat}" :items="companyInfoListItems">
					<template #maps-input-template="{item}">
						<div class="list-item-header">
							<p class="list-item-title">{{ item.title }}</p>
							<AppGoogleMapsInput :country-restriction="company?.country_code" :value="item.value" class="right-item text-wrapper maps-input-wrapper" :placeholder="item.placeholder" no-map @changed-address="item.callbackAddress" />
						</div>
					</template>
					<template #privacy-switch-template="{item}">
						<div class="list-item-header">
							<p class="list-item-title">{{ item.title }}</p>
							<AppSwitch class="right-item" :checked="item.value" @switched="item.callback" />
						</div>
					</template>
				</AppList>
			</AppDropdown>
		</div>
		<CompanyCountriesBottomsheet @selected-country="selectedCountry" @toggling-popup="v => activeCountriesBottomSheet = v" :active="activeCountriesBottomSheet" />
	</div>
</template>

<style scoped lang="scss">

</style>
<script>
import CompanyCountriesBottomsheet from "@/console-new/components/shared/CompanyCountriesBottomsheet.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppSelect from "@/shared/components/AppSelect.vue";
import AppBottomSheet from "@/shared/components/AppBottomSheet.vue";
import AppLoader from "@/shared/components/AppLoader.vue";
import AppDropdown from "@/shared/components/AppDropdown.vue";
import AppList from "@/shared/components/AppList.vue";
import AppGoogleMapsInput from "@/shared/components/AppGoogleMapsInput.vue";
import {useLanguageStore} from "@/shared/stores";
import {useCustomersStore, useUserStore} from "@/console-new/stores";
import API from "@/shared/helpers/API";
import AppButton from "@/shared/components/AppButton.vue";
import AppSwitch from "@/shared/components/AppSwitch.vue";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const customersStore = useCustomersStore();
		return {getString: languageStore.getString, COUNTRY, userStore, customersStore}
	},
	name: "CompanyInfoCollector",
	components: {AppSwitch, AppButton, AppGoogleMapsInput, CompanyCountriesBottomsheet, AppLoader, AppSelect, AppDropdown, AppList, AppBottomSheet, AppTextInput},
	emits: ['changed-company'],
	props: {
		allowNoVat: {
			type: Boolean,
			default: false,
		},
		disallowOtherCountries: {
			type: Boolean,
			default: false,
		},
		initialPrivacy: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		return {
			company: {
				country_code: null,
				vat: null,
				fiscal_name: null,
				fiscal_code: null,
				legal_address: null,
				delivery_address: null,
				sdi: null,
				pec: null,
				notes: null,
				privacy: this.initialPrivacy,
			},
			changingTextTimeout: null,
			collapsedDropdown: COUNTRY === 'IT',
			loadingInvoice: false,
			loadingVat: false,
			withoutVat: false,
			activeCountriesBottomSheet: false,
		}
	},
	computed: {
		companyInfoListItems() {
			let listItems = [
				{title: this.getString('fiscal_name'), callbackText: v => {this.company.fiscal_name = v; this.emitCompany() }, placeholder: this.getString('fiscal_name_example'), textInput: true, value: this.company.fiscal_name, backgroundColor: 'var(--color-alpha-1)', },
				{title: this.getString('legal_address'), callbackAddress: v=> {this.changedAddress('legal', v)}, customSlot: 'maps-input-template', placeholder: this.getString('address'), value: this.company.legal_address?.address, backgroundColor: 'var(--color-alpha-1)', },
				{title: this.getString('delivery_address'), callbackAddress: v=> {this.changedAddress('delivery', v)}, customSlot: 'maps-input-template', placeholder: this.getString('delivery_address'), value: this.company.delivery_address?.address }
			];

			let fiscalCodeItem = {title: this.getString('fiscal_code'), callbackText: v => {this.company.fiscal_code = v; this.emitCompany() }, placeholder: this.getString('fiscal_code_example'), textInput: true, value: this.company.fiscal_code, };

			if(this.withoutVat) {
				fiscalCodeItem.backgroundColor = 'var(--color-alpha-1)';
				listItems.unshift(fiscalCodeItem);
			} else {
				listItems.push(fiscalCodeItem);
			}

			if(!this.company?.country_code || this.company?.country_code === 'IT') {
				listItems.push({title: this.getString('sdi'), callbackText: v => {this.company.sdi = v; this.emitCompany() }, placeholder: this.getString('sdi_example'), textInput: true, value: this.company.sdi });
			}

			listItems = listItems.concat([
				{title: this.getString('pec'), callbackText: v => {this.company.pec = v; this.emitCompany() }, placeholder: this.getString('pec'), textInput: true, value: this.company.pec },
				{title: this.getString('marketing_consent'), customSlot: 'privacy-switch-template', value: this.company.privacy, callback: v => {this.company.privacy = v; this.emitCompany() }},
				{title: this.getString('notes'), callbackText: v => {this.company.notes = v; this.emitCompany() }, placeholder: this.getString('notes'), textInput: true, value: this.company.notes }
			]);
			return listItems;
		}
	},
	methods: {
		toggleCountryBottomSheet() {
			if(this.disallowOtherCountries) {
				return
			}
			this.activeCountriesBottomSheet = !this.activeCountriesBottomSheet;
		},
		selectedCountry(countryCode){
			this.company.country_code = countryCode;
			this.activeCountriesBottomSheet = false;
		},
		toggleWithoutVat(){
			this.withoutVat = !this.withoutVat;
			this.company.country_code = null;
			this.collapsedDropdown = !this.withoutVat;
			this.company.vat = this.withoutVat ? null : this.company.vat;
			this.emitCompany();
		},
		changedAddress(type, address) {
			this.company[type+'_address'] = address;
			this.emitCompany();
		},
		resetCompany() {
			this.company = {
				country_code: null,
				vat: null,
				fiscal_name: null,
				fiscal_code: null,
				legal_address: null,
				delivery_address: null,
				sdi: null,
				pec: null,
				notes: null,
			}
		},
		emitCompany() {
			this.$emit('changed-company', this.company, this.withoutVat);
		},
		requestCompanyInfo() {
			if(this.company?.country_code === 'OO') {
				return
			}
			this.blurActiveElement();
			this.loadingVat = true;
			let errorCallback = (err) => {
				this.loadingVat = false;
				console.warn('Company vat fetch error: ', err)
			}
			let finalCountry = this.company?.country_code || COUNTRY;
			API.init().getBusinessCompanyInfoFromVat(this.company.vat, finalCountry, this.userStore.currBusinessId, (resp) => {
				if(resp && resp.result === "OK" && resp.data) {
					if(resp.data?.company) {
						let address = this.addressDataFromPlace(resp.data.geodecoded_address) || resp.data.company.address || '';
						this.company.fiscal_name = resp.data.company.name || '';
						this.company.fiscal_code = '';
						this.company.sdi = resp.data.company.sdi || (finalCountry === 'IT' ? '0000000' : 'XXXXXXX');
						this.company.legal_address = address;
						this.company.delivery_address = address;
						this.emitCompany();
					} else {
						errorCallback('Not found');
					}
				} else {
					errorCallback('Rate limit');
				}
				this.loadingVat = false;
			}, err=>{
				errorCallback(err);
			});
		},
		changedVat(v) {
			if(this.loadingVat) { return }
			let startingVat = v || '';
			let italianVat = (!this.company?.country_code && COUNTRY === 'IT') || this.company?.country_code === 'IT';
			let sanitizedVat = italianVat ? (v || '').replace(/[^0-9]/g, '').slice(0, 11) : startingVat.replace(/\s/g, '');
			this.company.vat = sanitizedVat;
			this.emitCompany();
			if(sanitizedVat && sanitizedVat?.length && ((sanitizedVat?.length >= 8 && !italianVat) || (italianVat && sanitizedVat?.length >= 11))) {
				this.collapsedDropdown = false;
				this.requestCompanyInfo();
			} else if(COUNTRY === 'IT' && !italianVat && sanitizedVat && sanitizedVat?.length && sanitizedVat?.length > 6 && this.collapsedDropdown) {
				this.collapsedDropdown = false;
			}
		},
		timeoutChangedVat(v) {
			if(this.loadingVat) { return }
			this.company.vat = (v || '');
			if(this.changingTextTimeout) {
				clearTimeout(this.changingTextTimeout);
			}
			this.changingTextTimeout = setTimeout(() => {
				this.changedVat(v);
			}, 50);
		},
		blurActiveElement() {
			if(document.activeElement) {
				document.activeElement.blur();
			}
		},
		addressDataFromPlace(place) {
			let returningData = {};
			let locality = null;
			if(!place || !place?.address_components || !place?.address_components?.length || !place?.geometry || !place?.geometry?.location) {
				return '';
			}

			place.address_components.forEach(component => {

				if(component.types && component.types.length > 0) {
					if(component.types[0] === "administrative_area_level_1") {
						returningData.regione = component.long_name;
						if((!this.company.country_code && COUNTRY !== 'IT') || (this.company.country_code && this.company.country_code !== 'IT')) {
							if(!returningData?.provincia_codice) {
								returningData.provincia_codice = component.short_name;
							}
						}
					} else if(component.types[0] === "administrative_area_level_2") {
						returningData.provincia_codice = component.short_name;
						returningData.provincia = component.long_name;
					} else if(component.types[0] === "administrative_area_level_3") {
						returningData.comune = component.long_name;
					} else if(component.types[0] === "locality") {
						locality = component.long_name;
					} else if(component.types[0] === "route") {
						returningData.via = component.long_name;
					} else if(component.types[0] === "postal_code") {
						returningData.zip = component.long_name;
					} else if(component.types[0] === "street_number") {
						returningData.civico = component.long_name;
					} else if(component.types[0] === "country") {
						returningData.country = component.short_name;
					}
				}

			});

			if(!returningData.country) {
				returningData.country = this.company?.country_code || COUNTRY;
			}
			if(returningData.country !== 'IT') {
				returningData.provincia = returningData.provincia || locality || returningData.regione || null;
				returningData.comune = returningData.comune || returningData.provincia || null;
			}

			return {
				address: place.formatted_address,
				data: returningData,
				lat: place.geometry.location.lat,
				lng: place.geometry.location.lng
			};
		},
	},
}
</script>

<style scoped lang="scss">
.vat-dropdown-container {
	width: 100%;
	padding: 0 10px;
}
:deep(.dropdown-container) {
	.dropdown .dropdown-content {
		margin-top: 0;
		padding: 0;
		border-bottom-left-radius: 13px;
		border-bottom-right-radius: 13px;
	}
}
:deep(.list-container) {
	.list-item {
		//padding: 10px 14px;
	}
}
.inputs-containers {
	display: flex;
	flex-direction: column;
	align-items: flex-end;

	.input-container {
		width: 100%;
		display: flex;
		flex-wrap: wrap;
		gap: 12px;
		justify-content: space-between;
		align-items: center;
		position: relative;
		padding: 8px 12px;

		.ml-auto {
			margin-left: auto;
		}

		&:after {
			content: "";
			position: absolute;
			top: 100%;
			left: 0;
			height: 1px;
			width: 100%;
			background-color: var(--color-alpha);
		}

		&:last-child {
			&:after {
				display: none;
			}
		}

		&.vat-input-container {
			border: 1px solid var(--color-primary);
			border-radius: 13px;
			background-color: var(--color-bg-primary-1);

			.grey {
				font-size: 14px;
				color: var(--color-txt-2);
			}
			.primary {
				font-size: 14px;
				color: var(--color-primary);
				cursor: pointer;
			}
			&:after {
				display: none;
			}

			.vat-input {
				max-width: calc(100% - 102px) !important;
			}

		}
	}
}
.list-item-header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	flex-wrap: wrap;
	width: 100%;
	gap: 8px;

	.right-item {
		margin-left: auto;
	}
	&:deep(.map-input-container){
		.second-row {
			justify-content: flex-end;
			width: 100%;
			max-width: 280px;
		}
		.input-wrapper .input-optional {
			width: 100px;
		}
	}
	&:deep(.maps-input-wrapper){
		&.text-wrapper {
			width: 240px;
			.address-input {
				width: 100%;
			}
		}

		@media (max-width: 1250px) {
			&.text-wrapper {
				width: 190px;
				max-width: 190px;
			}
		}
	}
}
.wrapper-buttons-add {
	height: 100%;
	width: 100%;
	margin-bottom: 10px;
	.row-button-add {
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		align-items: center;
		width: 100%;
	}
	.base-button {
		height: 40px;
		width: 100%;
		max-width: unset;
		background-color: #e5edf6;
		border: none;
		&:hover {
			box-shadow: none;
		}
	}
}
.country-select {
	width: 60px;
	min-width: 60px;
	padding: 10px 0;
	margin-right: 10px;
	&.outline {
		border: none;
		background-color: var(--color-alpha-2);
		:deep(.btn-text p) {
			color: var(--color-txt);
		}
	}

	.no-pointer {
		pointer-events: none;
		cursor: default;
	}
}
</style>